import React from 'react';
import { useTranslation } from 'react-i18next';

import MoneyAmount from '@/shared/ui/components/money-amount';
import type { CreditDeclineBreakdownItem } from '@/shared/types/credit-decline';
import type { Currency } from '@/types';
import { getBreakdownLabelKey } from '@/modules/decline-ui/lib/decline-ui-helpers';
import { ChevronRightIcon } from '@/shared/ui/icons/chevron-right-icon';
import { ChevronLeftIcon } from '@/shared/ui/icons/chevron-left-icon';

import { DeclineBreakdownIcon } from './decline-breakdown-icons';

interface BreakdownItemCardProps {
  item: CreditDeclineBreakdownItem;
  currency: string | Currency;
  language: string;
  /**
   * i18n key for the sub-heading. Shown whenever set — including for
   * informational rows that are not tappable (e.g. external obligations).
   */
  descriptionKey?: string;
  /** When provided, the row becomes tappable and shows a chevron. */
  onActionClick?: () => void;
}

export function BreakdownItemCard({
  item,
  currency,
  language,
  descriptionKey,
  onActionClick,
}: BreakdownItemCardProps): React.JSX.Element {
  const { t } = useTranslation();
  const hasRange =
    item.amount.amountMax !== undefined &&
    item.amount.amountMax !== item.amount.amount;
  const isActionable = typeof onActionClick === 'function';
  const isRtl = language.startsWith('ar');

  const content = (
    <>
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <DeclineBreakdownIcon type={item.type} />
        <div className="min-w-0 flex-1">
          <p className="text-[12px] font-medium leading-4 tracking-[-0.18px] text-[#585B7A]">
            {t(getBreakdownLabelKey(item.type))}
          </p>
          <p className="text-[18px] font-semibold leading-6 tracking-[-0.27px] text-[#212442]">
            {hasRange ? (
              <>
                <MoneyAmount
                  amount={item.amount.amount}
                  currency={item.amount.currency || currency}
                  language={language}
                />
                <span className="mx-1">-</span>
                <MoneyAmount
                  amount={item.amount.amountMax!}
                  currency={item.amount.currency || currency}
                  language={language}
                />
              </>
            ) : (
              <MoneyAmount
                amount={item.amount.amount}
                currency={item.amount.currency || currency}
                language={language}
              />
            )}
          </p>
          {descriptionKey ? (
            <p className="mt-1 text-[14px] font-medium leading-5 tracking-[-0.21px] text-[#585B7A]">
              {t(descriptionKey)}
            </p>
          ) : null}
        </div>
      </div>
      {isActionable ? (
        <span className="ms-2 flex shrink-0 items-center self-center text-[#585B7A]">
          {isRtl ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </span>
      ) : null}
    </>
  );

  if (!isActionable) {
    return (
      <div className="flex w-full items-center rounded-[20px] bg-[#F4F6F9] p-4">
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onActionClick}
      className="flex w-full items-center rounded-[20px] bg-[#F4F6F9] p-4 text-start transition-colors hover:bg-[#EBEEF4] focus-visible:ring-2 focus-visible:ring-[#9600F1] focus-visible:ring-offset-2"
    >
      {content}
    </button>
  );
}
